import {
  KatanaPerpsSubscribeTypeAuthenticated,
  KatanaPerpsSubscribeTypePublic,
  WebSocketClient,
  WebSocketRequestUnsubscribeSubscription,
} from '@katanaperps/katana-perps-sdk';
// import './_websocketDebug';

import { getChainPrivateApi } from '#/api/apiCoreSdk';
import { triggerNotificationFromWebsocketMessage } from '#/components/layout/notifications/notificationsSdk';
import config from '#/config';
import { Chains, IGNORED_NONCE } from '#/config/constants';
import { waitAsync } from '#/lib/promiseTools';
import { debouncerByKey } from '#/lib/throttle';
import { getActiveChainFromState } from '#/reduxState/exchange/exchangeActiveChain';
import { isChainListed } from '#/reduxState/exchange/exchangeActiveChainUtils';
import { updateWebsocketState } from '#/reduxState/exchange/websocketStateReducer';
import { staticDispatch } from '#/reduxState/store';

import { WebsocketSubscriptionType, WebsocketResponse } from './websocketTypes';

const katanaEVMConfig = config.chains.katanaChain;
const baseRestApiURL = katanaEVMConfig.restApiUrl;

// For weird reason tests fail to import this method
const activeChain =
  process.env.NODE_ENV === 'test'
    ? 'katanaEVMChain'
    : getActiveChainFromState();

const getWebsocketUrl = (activeChain: Chains) =>
  activeChain === 'katanaEVMChain'
    ? katanaEVMConfig.webSocketApiUrl
    : (function () {
        console.warn('Chain has inactive Websocket');
        return '#error';
      })();

/**
 * Note that currently Websocket will never reconnect to different chain.
 * Re-connecting to different chain is only done after app reload.
 *
 * Also note that Websocket is disabled for unit testing.
 */
let client: WebSocketClient | undefined;

let subscriptions: {
  started: boolean;
  public?: KatanaPerpsSubscribeTypePublic;
  private?: KatanaPerpsSubscribeTypeAuthenticated;
}[] = [];

function removeSubscription(name: string) {
  subscriptions = subscriptions.filter(
    existing =>
      existing.public?.name !== name && existing.private?.name !== name
  );
}

function setAllSubscriptionsInactive() {
  subscriptions.forEach(subscription => {
    subscription.started = false;
  });
}

/** Guarantee we only call initialization once per wallet */
let lastInitializedWallet: string | undefined;

/** Initialize authenticated or public client based on `walletAddress` parameter */
const initializeClient = (walletAddress: string | undefined) => {
  lastInitializedWallet = walletAddress;
  if (client) {
    console.warn(
      '[ws] Client already intialized, must restartWebsocket to create another'
    );
    return;
  }
  client =
    process.env.NODE_ENV === 'test'
      ? ({} as WebSocketClient)
      : walletAddress
      ? new WebSocketClient({
          baseRestApiURL,
          baseWebSocketURL: getWebsocketUrl(activeChain),
          wallet: walletAddress,
          websocketAuthTokenFetch: (_client: unknown) =>
            getChainPrivateApi().getWsToken({
              nonce: IGNORED_NONCE,
              wallet: walletAddress,
            }),
          shouldReconnectAutomatically: true,
        })
      : new WebSocketClient({
          baseRestApiURL,
          baseWebSocketURL: getWebsocketUrl(activeChain),
          shouldReconnectAutomatically: true,
        });
};

/**
 * We have to resubscribe existing subscriptions after reconnecting websocket.
 * Eg. After wallet unlock.
 *
 * - If the client doesnt exist it will call startWebsocket which will initialize it.
 */
export async function restartWebsocket(walletAddress: string | undefined) {
  if (lastInitializedWallet === walletAddress) {
    console.log('[ws] Ignoring restart (Expected in React.StrictMode)');
    return;
  }
  if (client) {
    console.log('[ws] Restarting WebSocket');
    stopWebsocket();
  } else {
    console.log('[ws] Starting WebSocket');
  }

  setAllSubscriptionsInactive();

  // Wait until UI propagates possible updates to subscriptions
  // eg. user signed out and we no longer need wallet subsc.
  await waitAsync(100);
  await startWebsocket(walletAddress);
}

/** Check if client started ws initialization, it doesn't guarantee live connection */
export function isWebSocketInitialized() {
  return !!client;
}

export async function startWebsocket(walletAddress: string | undefined) {
  initializeClient(walletAddress);

  if (!client) {
    throw new Error('WebSocket problem - initialize');
  }
  console.log(
    `[ws] Connecting (wallet=${walletAddress?.substring(0, 5) || 'NO'}) ${
      client.isConnected ? '[Already Connected]' : ''
    }`
  );
  if (client.isConnected) {
    console.error('Trying to reconnect connected WebSocket');
    return;
  }
  client.onConnect(() => {
    if (!client) {
      console.error('[ws] Missing client (onConnect)');
      return;
    }

    console.log(`[ws] Connected ${!client.isConnected ? '[NO]' : ''}`);

    client.onMessage(message => subscriptionMessageHandler(message), true);

    // Always restart all subscriptions after WebSocket reconnect
    setAllSubscriptionsInactive();

    // Start subscriptions which are waiting in queue
    scheduleSubscriptionsRun();

    staticDispatch(updateWebsocketState(true));
  }, true);

  client.onDisconnect(() => {
    if (!client) {
      console.warn('[ws] Missing client (onDisconnect)');
      return;
    }
    console.log('[ws] Disconnected', !client.isConnected);
    setAllSubscriptionsInactive();
    staticDispatch(updateWebsocketState(false));
  }, true);

  const activeChain = getActiveChainFromState();
  if (isChainListed(activeChain)) {
    await client.connect();
  } else {
    console.warn('[ws] WebSocket will connect only for listed chain');
  }
}

/** Stops active WebSocket, so it can be connected with different wallet  */
function stopWebsocket() {
  if (!client) {
    console.warn('[ws] Missing client (stopWebsocket)');
    return;
  }

  console.log('[ws] Stopping..');
  // TODO: WebSocket: disconnect is not working with mock server, does it with real one ?
  client.onMessage(() => {
    console.warn('[ws] Ignoring response from stopped connection');
  }, true);
  client.onError(() => {
    console.warn('[ws] Ignoring error from stopped connection');
  }, true);
  client.onConnect(() => {
    console.error('[ws] Reopened closed connection');
  }, true);
  client.onDisconnect(() => {
    console.log('[ws] Disconnected (previous connection)');
  }, true);
  // Terminate websocket (this instance cannot be started again)
  client.disconnect(true);
  client = undefined;
  lastInitializedWallet = undefined;
}

const debounceByKey = debouncerByKey();

/**
 * Make sure we run subscription just once while initializing.
 * Otherwise also auth token fetch would be fetched multiple times.
 */
const scheduleSubscriptionsRun = () =>
  debounceByKey('wsSubscriptionsRun', 10, runWaitingSubscriptions);

function runWaitingSubscriptions(): void {
  if (!client || !client.isConnected) {
    console.log('Waiting for websocket connect');
    return;
  }
  const waitingWalletSubscriptions = subscriptions.filter(
    subscription => !subscription.started && subscription.private
  );
  if (waitingWalletSubscriptions.length > 0) {
    if (client.isPublicOnly) {
      // Might only be temporary issue until subscription is cleared
      console.warn(
        'Trying to connect to authenticatedSubscription using public client'
      );
    } else {
      client.subscribeAuthenticated(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        waitingWalletSubscriptions.map(subs => subs.private!).flat()
      );
      waitingWalletSubscriptions.forEach(subscription => {
        subscription.started = true;
      });
    }
  }

  const waitingPublicSubscriptions = subscriptions.filter(
    subscription => !subscription.started && subscription.public
  );
  if (waitingPublicSubscriptions.length > 0) {
    client.subscribePublic(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      waitingPublicSubscriptions.map(subs => subs.public!).flat()
    );
    waitingPublicSubscriptions.forEach(subscription => {
      subscription.started = true;
    });
  }
}

function subscriptionMessageHandler(message: WebsocketResponse): void {
  setTimeout(() => triggerNotificationFromWebsocketMessage(message));
  const callbacks = eventsCallbacks[message.type];
  if (!callbacks) {
    return;
  }
  Object.keys(callbacks).forEach(id => {
    const callback = callbacks[id];
    if (!callback) {
      console.error('subscriptionMessageHandler: Undefined callback');
      return;
    }
    callback(message);
  });
}

type EventCallback<TYPE> = {
  [event: string]: {
    [id: string]: WebsocketCallback<TYPE>;
  };
};

let callbackIndex = 1;
const eventsCallbacks: EventCallback<any> = {};

export function startPublicSubscription(
  subscription: KatanaPerpsSubscribeTypePublic
) {
  subscriptions.push({
    started: false,
    public: subscription,
  });
  scheduleSubscriptionsRun();
}

export function startAuthenticatedSubscription(
  subscription:
    | KatanaPerpsSubscribeTypeAuthenticated
    | KatanaPerpsSubscribeTypeAuthenticated[]
) {
  const subsArray = Array.isArray(subscription) ? subscription : [subscription];
  subscriptions.push(
    ...subsArray.map(subs => ({
      started: false,
      private: subs,
    }))
  );
  scheduleSubscriptionsRun();
}

export function stopSubscription(
  subscription: WebSocketRequestUnsubscribeSubscription
) {
  removeSubscription(subscription.name);
  if (!client?.isConnected) {
    return;
  }
  try {
    client?.unsubscribe([subscription]);
  } catch (error) {
    console.warn(error);
  }
}

export type WebsocketCallback<TYPE> = (
  payload: Extract<WebsocketResponse, { type: TYPE }>
) => void;

export function listenToWebsocket<TYPE extends WebsocketSubscriptionType>(
  eventType: TYPE,
  callback: WebsocketCallback<TYPE>
): number {
  callbackIndex += 1;
  if (!eventsCallbacks[eventType]) {
    eventsCallbacks[eventType] = {};
  }
  eventsCallbacks[eventType][callbackIndex] = callback;
  return callbackIndex;
}

export const stopListenFromWebsocket = (
  id: number,
  event?: WebsocketSubscriptionType
) => {
  if (event) {
    // if we have event, we can delete fastest way
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete eventsCallbacks[event][id];
    return;
  }
  Object.keys(eventsCallbacks).some(event => {
    if (!eventsCallbacks[event] || !eventsCallbacks[event][id]) {
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete eventsCallbacks[event][id];
    return true;
  });
};
